import React, { createContext, useState, useContext, useEffect } from 'react'

const OnboardingContext = createContext()

export function OnboardingProvider({ children }) {
  // const [onboardingUserUrl] = useState("http://localhost:8005/onboarding")
  // const [onboardingInputUrl] = useState("http://localhost:8003/onboarding")
  const [onboardingUserUrl, setOnboardingUserUrl] = useState(null)
  const [onboardingInputUrl, setOnboardingInputUrl] = useState(null)
  const [doOnboarding, setDoOnboarding] = useState(false)

  const [token] = useState(new URLSearchParams(window.location.search).get("token"))

  const [companyName, setCompanyName] = useState('')
  const [companyShortName, setCompanyShortName] = useState('')
  const [country, updateCountry] = useState('') 
  const [language, updateLanguage] = useState('')
  const [timezone, setTimezone] = useState('')
  const [currency, setCurrency] = useState('')
  const [dateFormat, updateDateFormat] = useState('')
  const [numberFormat, updateNumberFormat] = useState('')
  const [companyLogo, setCompanyLogo] = useState('')
  const [files, setFiles] = useState([])
  const [filesCurrentAction, setFilesCurrentAction] = useState('idle')
  const [ready, setReady] = useState(false)
  
  const [userFirstName, setUserFirstName] = useState('')
  const [userLastName, setUserLastName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userPassword, setUserPassword] = useState('')
  const [userCountry, setUserCountry] = useState('')
  const [userLanguage, setUserLanguage] = useState('')
  const [userDateFormat, setUserDateFormat] = useState('')
  const [userNumberFormat, setUserNumberFormat] = useState('')
  const [userProfilePicture, setUserProfilePicture] = useState('')

  const [collaborators, setCollaborators] = useState(Array.from({ length: 6 }, () => ({firstName: '', lastName: '', email: '', language: `${language}`})))

  const [latestStep, setLatestStep] = useState(1)

    useEffect(() => {
        if (token !== null) {
            setOnboardingInputUrl(`https://input.optimahr.app/onboarding`)
            setOnboardingUserUrl(`https://user.optimahr.app/onboarding`)
        }
    }, [onboardingUserUrl, onboardingInputUrl])

  useEffect(() => {
    if (token && onboardingUserUrl) {
      fetch(`${onboardingUserUrl}/need?token=${token}`).then(response => response.status === 200)
      .then(ok => setDoOnboarding(ok))
      .catch(() => setDoOnboarding(false))
    }
  }, [token, onboardingUserUrl])

  const updateLatestStep = (step) => {
    if (step > latestStep) setLatestStep(step)
  }

  const setCountry = (country) => {
    updateCountry(country)
    if (latestStep < 3 && country !== "") setUserCountry(country)
  }

  const setLanguage = (language) => {
    updateLanguage(language)
    if (latestStep < 3 && language !== "") setUserLanguage(language)
  }

  const setDateFormat = (dateFormat) => {
    updateDateFormat(dateFormat)
    if (latestStep < 3 && dateFormat !== "") setUserDateFormat(dateFormat)
  }

  const setNumberFormat = (numberFormat) => {
    updateNumberFormat(numberFormat)
    if (latestStep < 3 && numberFormat !== "") setUserNumberFormat(numberFormat)
  }

  useEffect(() => {
    if (latestStep < 3) {
      setCollaborators(collaborators.map(
        c => ({...c, language: `${language}`})
      ))
    }
  }, [language])

  const registerCompany = async () => {
    const companyData = {
      name: companyName,
      shortname: companyShortName,
      language: language === "French" ? 'fr-FR' : 'en-GB',
      // currency: currency === "Euro" ? 'EUR' : 'USD',
      numberformat: numberFormat,
      dateformat: dateFormat,
      // timezone,
      flagimage: companyLogo !== '' ? companyLogo : null
    }

    const data = {
      method: 'POST',
      body: JSON.stringify(companyData),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }

    const api = `${onboardingUserUrl}/company?token=${token}` 
    const response = await fetch(api, data)
    return await response.text()
  }

  async function registerUser(user, self) {
    const data = {
      method: 'POST',
      body: JSON.stringify(user),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }

    const api = `${onboardingUserUrl}/register?token=${token}${self ? '&self=true' : ''}` 
    return fetch(api, data).then(response => response.text())
  }

  async function registerSelfUser() {
    const user = {
      firstname: userFirstName, 
      lastname: userLastName,
      email: userEmail,
      password: userPassword,
      profile: "", // defined on backend
      // country: userCountry,
      language: userLanguage === "French" ? 'fr-FR' : 'en-GB',
      dateformat: userDateFormat,
      numberformat: userNumberFormat,
      avatar: userProfilePicture.length > 0 ? userProfilePicture : ""
    }
    await registerUser(user, true)
  }

  async function registerCollaborators() {
    const promises = []
    collaborators.map(async (user) => {
      user.language = user.language === "French" ? 'fr-FR' : 'en-GB'
      user.profile = ""
      user.avatar = null
      promises.push(registerUser(user))
    })
    
    await Promise.all(promises)
  }

  async function finishOnboarding() {
    fetch(`${onboardingUserUrl}/finish?token=${token}`, {method: 'POST'})
  }

  return (
    <OnboardingContext.Provider
      value={{
        doOnboarding,
        finishOnboarding,
        onboardingInputUrl,
        companyName,
        setCompanyName,
        companyShortName,
        setCompanyShortName,
        country,
        setCountry,
        language,
        setLanguage,
        timezone,
        setTimezone,
        currency,
        setCurrency,
        dateFormat,
        setDateFormat,
        numberFormat,
        setNumberFormat,
        companyLogo,
        setCompanyLogo,
        files,
        setFiles,
        filesCurrentAction,
        setFilesCurrentAction,
        userFirstName,
        setUserFirstName,
        userLastName,
        setUserLastName,
        userEmail,
        setUserEmail,
        userPassword,
        setUserPassword,
        userCountry,
        setUserCountry,
        userLanguage,
        setUserLanguage,
        userDateFormat,
        setUserDateFormat,
        userNumberFormat,
        setUserNumberFormat,
        userProfilePicture,
        setUserProfilePicture,
        latestStep,
        updateLatestStep,
        collaborators,
        setCollaborators,
        ready,
        setReady,
        registerCompany, 
        registerSelfUser,
        registerCollaborators
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  return useContext(OnboardingContext)
}
